/**
 * openingArrayOps — the unit of a door / window / opening edit is the ELEMENT,
 * not the array.
 *
 * Every caller knows exactly what it is doing: a style apply has its target
 * ids, a delete has the canonical id, a panel edit has the element it patched,
 * an add has the new element. Saving the whole array threw that away, and the
 * scan document is not live in the viewer — so a session open for a while wrote
 * back an array predating everyone else's adds, deletes and edits and silently
 * reverted them. Deleting a canonical record whose room copy is still live is
 * also how an opening becomes invisible (#2107).
 *
 * The array-shaped writer was not a decision about concurrency: it was added
 * for the wall-drag cascade (`2877a3a75`), where every opening on the dragged
 * wall really is recomputed together. But "every opening on the wall" is still
 * a list of elements — the whole array also carried every OTHER opening, from
 * the same stale copy, so a wall drag put back doors deleted elsewhere. The
 * wall edits now upsert the openings on the wall (`wallOpeningUpsertOps`), and
 * no caller writes an array.
 *
 * An op set is `{ upsert?, add?, remove? }`:
 *
 *   - `upsert` — write this element over the server's copy. Two sessions
 *     editing the same door is last-write-wins on that door alone. **If the
 *     server no longer has it, SKIP: it was deleted, and an edit must never
 *     resurrect it.**
 *   - `add` — a new element, appended. A fresh id, so never a resurrection.
 *   - `remove` — ids to drop.
 *
 * PURE, and used on BOTH sides of the write: the host applies the same ops to
 * its local array, and the transaction applies them to the server's.
 */

const byId = (elements) => {
  const map = new Map();
  for (const element of elements || []) {
    if (element?.id) map.set(element.id, element);
  }
  return map;
};

/** True when the op set would not change anything. */
export function isEmptyOps(ops) {
  return (
    !ops ||
    ((ops.upsert?.length || 0) === 0 &&
      (ops.add?.length || 0) === 0 &&
      (ops.remove?.length || 0) === 0)
  );
}

/**
 * Apply an op set to an opening array.
 *
 * @param {Array} array the array to apply to — the server's inside the
 *   transaction, the host's own copy locally.
 * @param {{upsert?: Array, add?: Array, remove?: string[]}} ops
 * @returns {{array: Array, skippedUpsertIds: string[]}} `skippedUpsertIds` are
 *   elements this edit targeted that the array no longer has: deleted
 *   elsewhere, and deliberately not re-created.
 */
export function applyOpeningOps(array, ops) {
  const { upsert = [], add = [], remove = [] } = ops || {};
  const removed = new Set(remove);
  const upsertById = byId(upsert);
  const present = new Set();

  const next = [];
  for (const element of array || []) {
    const id = element?.id;
    if (id && removed.has(id)) continue;
    if (id) present.add(id);
    next.push(id && upsertById.has(id) ? upsertById.get(id) : element);
  }

  const skippedUpsertIds = [];
  for (const id of upsertById.keys()) {
    if (!present.has(id)) skippedUpsertIds.push(id);
  }

  for (const element of add) {
    if (element?.id && present.has(element.id)) continue;
    next.push(element);
  }

  return { array: next, skippedUpsertIds };
}

/** The structure's opening arrays, by their Firestore field names. */
const OPENING_KEYS = ['doors', 'windows', 'openings'];

/**
 * Upsert op sets for the openings an edit moved, keyed like the structure
 * document. A key with nothing moved is left out, so an edit that moved
 * nothing writes nothing.
 *
 * @param {{doors?: Array, windows?: Array, openings?: Array}} movedByKey
 * @returns {Object} `{ doors?: {upsert}, windows?: {upsert}, openings?: {upsert} }`
 */
export function upsertOpsByKey(movedByKey) {
  const ops = {};
  for (const key of OPENING_KEYS) {
    const moved = movedByKey?.[key];
    if (moved?.length) ops[key] = { upsert: moved };
  }
  return ops;
}

/**
 * Upsert op sets for every opening hosted on one wall — what a wall edit
 * moved, read from the arrays the edit has already re-seated.
 *
 * @param {{doors?: Array, windows?: Array, openings?: Array}} arrays
 * @param {string} wallId the structure wall that moved.
 * @returns {Object} as `upsertOpsByKey`.
 */
export function wallOpeningUpsertOps(arrays, wallId) {
  const moved = {};
  for (const key of OPENING_KEYS) {
    moved[key] = (arrays?.[key] || []).filter(
      (element) => element?.parentWallIds?.[0] === wallId
    );
  }
  return upsertOpsByKey(moved);
}

/**
 * Convenience for the common host shape: apply ops and return just the array.
 * The host's local copy and the server's are updated by the same function, so
 * they cannot drift in how an op is interpreted.
 */
export function applyOpeningOpsToArray(array, ops) {
  return applyOpeningOps(array, ops).array;
}
